import { useState } from "react";
import { ArrowRight, ChevronRight, Sparkles } from "lucide-react";
import { cn } from "@/utils/helpers";
import { statePrimary } from "./CalendarStates";

const KEY = (planId: string) => `co.planReviewed.${planId}`;

function readReviewed(planId: string): boolean {
  try {
    return window.localStorage.getItem(KEY(planId)) === "1";
  } catch {
    return false;
  }
}

/** "this morning", "yesterday", "on Fri 5 Feb": when the plan landed. */
function whenPlanned(iso: string, now: number): string {
  const at = new Date(iso);
  const day = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const days = Math.round((day(new Date(now)) - day(at)) / 86_400_000);
  if (days <= 0) return at.getHours() < 12 ? "this morning" : "today";
  if (days === 1) return "yesterday";
  const weekday = at.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
  const month = at.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
  return `on ${weekday} ${at.getUTCDate()} ${month}`;
}

/**
 * Calendar / Plan review strip: over a month the agent has just planned, the two
 * steps it asks for, in order. Review the plan (add, move or remove posts while
 * the ideas can still follow), then pick an idea for each post. "Approve plan"
 * is the team saying the plan stands; it is remembered in this browser, since
 * the plan itself is live as soon as it lands.
 */
export default function PlanReviewStrip({
  planId,
  plannedAt,
  now,
  picked,
  total,
  onPickIdeas,
}: {
  planId: string;
  plannedAt: string;
  now: number;
  picked: number;
  total: number;
  onPickIdeas: () => void;
}) {
  const [reviewed, setReviewed] = useState(() => readReviewed(planId));
  const approve = () => {
    setReviewed(true);
    try {
      window.localStorage.setItem(KEY(planId), "1");
    } catch {
      // Private mode: the strip moves on for this visit only.
    }
  };
  const step = (n: number, label: string, on: boolean) => (
    <span className="flex items-center gap-2">
      <span
        className={cn(
          "grid size-[18px] shrink-0 place-items-center rounded-full text-[10.5px] font-bold leading-none tabular-nums",
          on ? "bg-co-accent-fill text-co-on-accent" : "bg-[var(--co-tonal)] text-co-text2",
        )}
      >
        {n}
      </span>
      <span className={cn("text-[13px] font-medium", on ? "text-co-text" : "text-co-text2")}>{label}</span>
    </span>
  );
  return (
    <div className="@container/plan min-w-0">
      <div
        role="region"
        aria-label="Review the plan"
        className="flex flex-col gap-3 rounded-panel border border-co-accent-border bg-co-surface-solid px-4 py-3.5 @min-[56rem]/plan:flex-row @min-[56rem]/plan:items-center @min-[56rem]/plan:gap-4"
      >
        <span className="flex min-w-0 items-start gap-3 @min-[56rem]/plan:flex-1 @min-[56rem]/plan:items-center">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-co-accent @min-[56rem]/plan:mt-0" aria-hidden="true" />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="text-[14px] font-semibold text-co-text">
              Planned {whenPlanned(plannedAt, now)}. Check the plan, then pick an idea for each post.
            </span>
            <span className="text-[13px] text-co-text2">Add, move or remove posts first; the ideas follow their post.</span>
          </span>
        </span>
        <span className="flex flex-wrap items-center gap-x-4 gap-y-2 @min-[56rem]/plan:shrink-0">
          <span className="flex flex-wrap items-center gap-2">
            {step(1, "Review plan", !reviewed)}
            <ChevronRight className="size-3.5 text-co-faint" aria-hidden="true" />
            {step(2, `Pick ideas · ${picked} of ${total}`, reviewed)}
          </span>
          <button
            type="button"
            className={cn(statePrimary, "ms-auto @min-[56rem]/plan:ms-0")}
            onClick={reviewed ? onPickIdeas : approve}
          >
            {reviewed ? "Pick ideas" : "Approve plan"}
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </button>
        </span>
      </div>
    </div>
  );
}
